import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { roleService } from '../../services/api';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({ name: '', label: '' });
  const [editingRole, setEditingRole] = useState(null);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await roleService.getAppRoles();
      if (result.success) {
        setRoles(result.data || []);
      } else {
        setError(result.message || 'Errore nel caricamento dei ruoli');
      }
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Errore nel caricamento dei ruoli');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const resetForm = () => {
    setFormData({ name: '', label: '' });
    setEditingRole(null);
  };

  const handleEdit = (role) => {
    if (role.isSystemRole) return;
    setEditingRole(role);
    setFormData({ name: role.name, label: role.label || role.name });
    setSuccess('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formData.name.trim();
    const label = formData.label.trim();

    if (!name) {
      setError('Il nome del ruolo è obbligatorio');
      return;
    }

    if (!label) {
      setError('L\'etichetta del ruolo è obbligatoria');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = editingRole
        ? await roleService.updateAppRole(editingRole.id, name, label)
        : await roleService.createAppRole(name, label);

      if (result.success) {
        setSuccess(result.message || 'Ruolo salvato con successo');
        resetForm();
        await loadRoles();
      } else {
        setError(result.message || 'Errore nel salvataggio del ruolo');
      }
    } catch (err) {
      console.error('Error saving role:', err);
      setError('Errore nel salvataggio del ruolo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (role.isSystemRole) return;
    if (!window.confirm(`Eliminare il ruolo "${role.name}"? Gli utenti perderanno l'assegnazione.`)) return;

    setDeletingId(role.id);
    setError('');
    setSuccess('');
    try {
      const result = await roleService.deleteAppRole(role.id);
      if (result.success) {
        setSuccess(result.message || 'Ruolo eliminato con successo');
        await loadRoles();
      } else {
        setError(result.message || 'Errore nell\'eliminazione del ruolo');
      }
    } catch (err) {
      console.error('Error deleting role:', err);
      setError('Errore nell\'eliminazione del ruolo');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <p className="text-muted">
        Crea, rinomina ed elimina i ruoli personalizzati. I ruoli di sistema sono protetti.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Form onSubmit={handleSubmit} className="mb-4">
        <div className="d-flex gap-2 align-items-end">
          <Form.Group className="flex-grow-1 mb-0">
            <Form.Label>{editingRole ? 'Modifica ruolo' : 'Nuovo ruolo'}</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="es. istruttore"
              disabled={saving}
            />
          </Form.Group>
          <Form.Group className="flex-grow-1 mb-0">
            <Form.Label>Etichetta visibile</Form.Label>
            <Form.Control
              type="text"
              value={formData.label}
              onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
              placeholder="es. Istruttore"
              disabled={saving}
            />
          </Form.Group>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Salvataggio...
              </>
            ) : (
              <>
                <i className={`fas ${editingRole ? 'fa-save' : 'fa-plus'} me-2`}></i>
                {editingRole ? 'Aggiorna' : 'Crea'}
              </>
            )}
          </Button>
          {editingRole && (
            <Button type="button" variant="outline-secondary" onClick={resetForm} disabled={saving}>
              Annulla
            </Button>
          )}
        </div>
      </Form>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : roles.length > 0 ? (
        <div className="table-responsive">
          <Table striped bordered hover className="align-middle">
            <thead>
              <tr>
                <th>Etichetta</th>
                <th>Nome tecnico</th>
                <th>Tipo</th>
                <th>Creato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td><strong>{role.label || role.name}</strong></td>
                  <td><code>{role.name}</code></td>
                  <td>
                    {role.isSystemRole ? (
                      <Badge bg="secondary">Sistema</Badge>
                    ) : (
                      <Badge bg="info">Personalizzato</Badge>
                    )}
                  </td>
                  <td>{role.createdAt ? new Date(role.createdAt).toLocaleString('it-IT') : '-'}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleEdit(role)}
                      disabled={role.isSystemRole || saving}
                    >
                      <i className="fas fa-edit"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(role)}
                      disabled={role.isSystemRole || deletingId === role.id}
                    >
                      {deletingId === role.id ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        <i className="fas fa-trash-alt"></i>
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center text-muted py-4">Nessun ruolo trovato</div>
      )}
    </div>
  );
};

export default RoleManagement;
